import { Play, Pause, RotateCcw, ArrowLeft, Minimize, Sparkles } from "lucide-react";
import { FsHud, ArtifactHudChip } from "./ui/GameHud";
import { KeyBadge } from "./ui/KeyBadge";

/**
 * Fullscreen-only HUD overlay — the giant block of absolutely-positioned
 * chrome that's only mounted when the canvas is in fullscreen mode.
 *
 * Extracted from `GameCanvas.jsx` on 2026-05-13. Composed of four
 * sub-pieces, kept inside a single component so the parent only has to
 * mount one tree:
 *
 *   - Top row: level chip (centred-ish on the left) + Exit FS button
 *   - Left column: Lives / Score / Best stack
 *   - Right artifact column: equipped paddle + ball chips (md+ only)
 *   - Right action column: Pause / Restart / Skins / Quit (md+ only)
 *
 * Renders nothing when `isFullscreen` is false so the parent can mount
 * it unconditionally.
 */
export function FullscreenHud({
  isFullscreen,
  hudPrimaryLabel,
  levelNumDisplay,
  levelNameDisplay,
  lives,
  score,
  bestScore,
  artifactHud,
  artifactsCompetitive,
  paused,
  gameOver,
  won,
  onToggleFullscreen,
  onTogglePause,
  onRestart,
  onOpenSkinsPanel,
  onExit,
}) {
  if (!isFullscreen) return null;
  return (
    <>
      {/* Top row: Level chip (centred-ish on the left) + Exit FS button */}
      <div
        className="absolute top-4 left-4 right-4 flex items-start justify-between gap-3 pointer-events-none"
        data-testid="game-hud-fullscreen"
      >
        <FsHud
          label={hudPrimaryLabel}
          value={levelNumDisplay}
          subValue={levelNameDisplay}
          color="#00ffff"
        />
        <button
          onClick={onToggleFullscreen}
          className="pointer-events-auto glass px-3 py-2 border border-[#00ffff]/60 text-[#00ffff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#00ffff]/10"
          data-testid="fullscreen-exit-inline"
        >
          <span className="inline-flex items-center gap-2">
            <Minimize className="w-3.5 h-3.5" /> Exit FS
          </span>
        </button>
      </div>

      {/* Left column: Lives / Score / Best stacked vertically — sits
          clear of the play-field so live scoring doesn't compete with
          the canvas content. Vertically centered so it stays balanced
          on tall portrait phones and short landscape laptops. */}
      <div
        className="absolute left-4 top-1/2 -translate-y-1/2 flex flex-col gap-2 pointer-events-none"
        data-testid="game-hud-fullscreen-left"
      >
        <FsHud label="Lives" value={"♥".repeat(Math.max(0, lives))} color="#ff007f" />
        <FsHud label="Score" value={score.toLocaleString()} color="#ffd700" />
        <FsHud label="Best" value={(bestScore || 0).toLocaleString()} color="#b026ff" />
      </div>

      {/* Right-side artifact HUD column — sits below the moonstone wallet
          chip (right-4 top-16) and above the control button column. Hidden
          on small screens where the top-of-canvas chip already covers this
          info; only shown when at least one artifact is equipped AND
          artifacts aren't disabled by competitive mode. */}
      {artifactHud && (artifactHud.paddle || artifactHud.ball) && !artifactsCompetitive && (
        <div
          className="absolute right-4 top-32 hidden md:flex flex-col gap-2 pointer-events-none w-[200px] max-w-[28vw]"
          data-testid="game-hud-fullscreen-artifacts"
        >
          {artifactHud.paddle && <ArtifactHudChip slot="paddle" art={artifactHud.paddle} />}
          {artifactHud.ball && <ArtifactHudChip slot="ball" art={artifactHud.ball} />}
        </div>
      )}

      {/* Right column: PC-only fullscreen controls. Mirrors the left
          HUD column so the canvas stays centered. Hidden on touch-sized
          devices where the bottom bar already covers these actions. */}
      <div
        className="absolute right-4 top-1/2 -translate-y-1/2 hidden md:flex flex-col gap-2 pointer-events-auto"
        data-testid="game-hud-fullscreen-right"
      >
        <button
          type="button"
          onClick={onTogglePause}
          disabled={gameOver || won}
          className="glass px-3 py-2 border border-[#00ffff]/60 text-[#00ffff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#00ffff]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="fs-pause-btn"
          title={paused ? "Resume (P)" : "Pause (P)"}
        >
          {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
          {paused ? "Resume" : "Pause"}
          <KeyBadge k="P" color="#00ffff" />
        </button>
        <button
          type="button"
          onClick={onRestart}
          className="glass px-3 py-2 border border-[#ffd700]/60 text-[#ffd700] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#ffd700]/10 flex items-center gap-2"
          data-testid="fs-restart-btn"
          title="Restart current level (R)"
        >
          <RotateCcw className="w-3.5 h-3.5" />
          Restart
          <KeyBadge k="R" color="#ffd700" />
        </button>
        <button
          type="button"
          onClick={onOpenSkinsPanel}
          disabled={gameOver || won}
          className="glass px-3 py-2 border border-[#b026ff]/60 text-[#b026ff] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#b026ff]/10 disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          data-testid="fs-skins-btn"
          title="Switch skin — opens pause panel (K)"
        >
          <Sparkles className="w-3.5 h-3.5" />
          Skins
          <KeyBadge k="K" color="#b026ff" />
        </button>
        <button
          type="button"
          onClick={onExit}
          className="glass px-3 py-2 border border-[#ff007f]/60 text-[#ff007f] font-mono-cosmic text-[10px] tracking-[0.25em] uppercase hover:bg-[#ff007f]/10 flex items-center gap-2"
          data-testid="fs-quit-btn"
          title="Quit to menu (Q)"
        >
          <ArrowLeft className="w-3.5 h-3.5" />
          Quit
          <KeyBadge k="Q" color="#ff007f" />
        </button>
      </div>
    </>
  );
}

// `KeyBadge` lives in `./ui/KeyBadge.jsx` — both windowed callers (the
// onboarding hotkey-tip banner in GameCanvas) and this FS column import
// from the neutral location, so the dependency graph stays acyclic.
